import React from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { BootErrorScreen } from '@/components/BootErrorScreen'
import { getSdkUrl } from '@/lib/sdk-official/loader'

interface SdkGateProps {
  status: 'loading' | 'error' | 'ready'
  error?: string | null
  onRetry?: () => void
  children: React.ReactNode
}

/* `onRetry` is kept in the props and unused on purpose: the error screen
   reloads the page, which is a stronger retry than re-running the loader with
   whatever half-state the first attempt left behind. */
export const SdkGate: React.FC<SdkGateProps> = ({ status, error, children }) => {
  const { t } = useTranslation('common')

  if (status === 'ready') {
    return <>{children}</>
  }

  /*
   * The same screen the community bootstrap uses, because this is the same
   * failure one step earlier: the browser could not fetch something from the
   * API's domain.
   *
   * It used to be a generic box with whatever message the loader threw —
   * no address, nothing to copy, nothing for the person to check. On a
   * corporate network that is the difference between a report that can be
   * solved and one that says "the site is broken".
   */
  if (status === 'error') {
    return (
      <BootErrorScreen
        failure={{ kind: 'unreachable', url: getSdkUrl(), detail: error || undefined }}
        domain={window.location.hostname}
      />
    )
  }

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
      <p className="text-sm text-muted-foreground">
        {t('errors.sdkLoading')}
      </p>
    </div>
  )
}
